import {
  packageVersionTasks,
  servicePackageVersions,
  servicePackages,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Base schemas
export const selectServicePackageSchema = createSelectSchema(servicePackages)
export const insertServicePackageSchema = createInsertSchema(
  servicePackages,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updateServicePackageSchema = insertServicePackageSchema.partial()

// Service package version schemas
export const selectServicePackageVersionSchema = createSelectSchema(
  servicePackageVersions,
)
export const insertServicePackageVersionSchema = createInsertSchema(
  servicePackageVersions,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updateServicePackageVersionSchema =
  insertServicePackageVersionSchema.partial()

// Package version task schemas
export const selectPackageVersionTaskSchema =
  createSelectSchema(packageVersionTasks)
export const insertPackageVersionTaskSchema = createInsertSchema(
  packageVersionTasks,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updatePackageVersionTaskSchema =
  insertPackageVersionTaskSchema.partial()

// Schema with relations
export const selectServicePackageWithVersionsSchema =
  selectServicePackageSchema.extend({
    versions: z.array(selectServicePackageVersionSchema),
  })

export const selectServicePackageVersionWithTasksSchema =
  selectServicePackageVersionSchema.extend({
    servicePackage: selectServicePackageSchema,
    packageVersionTasks: z.array(
      selectPackageVersionTaskSchema.extend({
        maintenanceTask: z.object({
          id: z.number(),
          name: z.string(),
          description: z.string().nullable(),
          category: z.string().nullable(),
        }),
      }),
    ),
  })

// Service package version with tasks but without circular servicePackage reference (for list views)
export const selectServicePackageVersionWithTasksForListSchema =
  selectServicePackageVersionSchema.extend({
    packageVersionTasks: z.array(
      selectPackageVersionTaskSchema.extend({
        maintenanceTask: z.object({
          id: z.number(),
          name: z.string(),
          description: z.string().nullable(),
          category: z.string().nullable(),
        }),
      }),
    ),
  })

// Service package with versions that include tasks
export const selectServicePackageWithVersionsAndTasksSchema =
  selectServicePackageSchema.extend({
    versions: z.array(selectServicePackageVersionWithTasksSchema),
  })

// Service package with versions and tasks for list views (no circular reference)
export const selectServicePackageWithVersionsAndTasksForListSchema =
  selectServicePackageSchema.extend({
    versions: z.array(selectServicePackageVersionWithTasksForListSchema),
  })

// Type exports
export type ServicePackage = z.infer<typeof selectServicePackageSchema>
export type ServicePackageInsert = z.infer<typeof insertServicePackageSchema>
export type ServicePackageUpdate = z.infer<typeof updateServicePackageSchema>
export type ServicePackageWithVersions = z.infer<
  typeof selectServicePackageWithVersionsSchema
>
export type ServicePackageWithVersionsAndTasks = z.infer<
  typeof selectServicePackageWithVersionsAndTasksSchema
>

export type ServicePackageVersion = z.infer<
  typeof selectServicePackageVersionSchema
>
export type ServicePackageVersionInsert = z.infer<
  typeof insertServicePackageVersionSchema
>
export type ServicePackageVersionUpdate = z.infer<
  typeof updateServicePackageVersionSchema
>
export type ServicePackageVersionWithTasks = z.infer<
  typeof selectServicePackageVersionWithTasksSchema
>

export type PackageVersionTask = z.infer<typeof selectPackageVersionTaskSchema>
export type PackageVersionTaskInsert = z.infer<
  typeof insertPackageVersionTaskSchema
>
export type PackageVersionTaskUpdate = z.infer<
  typeof updatePackageVersionTaskSchema
>
